import { isStringIndexedInterface, } from "Qrc/lib/generated/typeguard";
import { GraphNodeProcessingState, TableType, } from "Qrc/lib/generated/enum";
import { isSignatureWst, } from "./workstep_util";
import { createNodeUserDataStringIndex, isCompatibleToNodeUserDataEntryImpl, isNodeUserDataEntryType, nodeUserDataEntries, nodeUserDataProcessingStateMap, } from "./userdata_config";
import { assert, assertDebug, getKeysOfObject, isEqual, isString, } from "./utils";
import { min as computeMinProcessingState, } from "./graphnodeprocessingstate_utils";
import { getTable, } from "./table_utils";
export function nodeUserDataContextForVertex(vertex) {
    return {
        workStepType: wsi4.node.workStepType(vertex),
        processType: wsi4.node.processType(vertex),
        isSignatureNode: isSignatureWst(wsi4.node.workStepType(vertex), wsi4.graph.article(vertex).map(v => wsi4.node.workStepType(v))),
        isInitialNode: wsi4.node.isInitial(vertex),
    };
}
export function getNodeUserDataEntry(key, vertex, userData) {
    if (!isCompatibleToNodeUserDataEntry(key, vertex)) {
        return wsi4.throwError("Get UserData entry \"" + key + "\" for incompatible vertex with key \"" + wsi4.util.toKey(vertex) + "\"");
    }
    const context = nodeUserDataContextForVertex(vertex);
    userData = userData !== undefined ? userData : wsi4.node.userData(vertex);
    const value = userData[createNodeUserDataStringIndex(key, context)];
    return isNodeUserDataEntryType(key, value) ? value : undefined;
}
export function getNodeUserDataEntryOrThrow(key, vertex, userData) {
    const value = getNodeUserDataEntry(key, vertex, userData);
    return isNodeUserDataEntryType(key, value) ? value : wsi4.throwError("No UserData entry for key \"" + key + "\" and vertex \"" + wsi4.util.toKey(vertex) + "\"");
}
export function createUpdatedNodeUserData(key, vertex, value, userData) {
    if (!isCompatibleToNodeUserDataEntry(key, vertex)) {
        return wsi4.throwError("Set UserData entry \"" + key + "\" for incompatible vertex with key \"" + wsi4.util.toKey(vertex) + "\"");
    }
    const context = nodeUserDataContextForVertex(vertex);
    userData = userData !== undefined ? userData : wsi4.node.userData(vertex);
    userData[createNodeUserDataStringIndex(key, context)] = value;
    return userData;
}
export const userDataAccess = {
    self: 1,
    reaching: 2,
    reachable: 4,
    article: 8,
};
export const accessAllUserData = userDataAccess.self | userDataAccess.reaching | userDataAccess.reachable;
export const accessSelfAndReachable = userDataAccess.self | userDataAccess.reachable;
const isCloseNodeUserDataFunctionMap = {
    sheetMaterial: (lhs, rhs) => lhs.identifier === rhs.identifier,
    comment: isEqual,
    materialCostsPerPiece: isEqual,
    setupTime: isEqual,
    unitTimePerPiece: isEqual,
    cuttingGas: (lhs, rhs) => lhs.identifier === rhs.identifier,
    packaging: (lhs, rhs) => lhs.identifier === rhs.identifier,
    transportDistance: isEqual,
    importId: isEqual,
    attachments: isEqual,
    testReportRequired: isEqual,
    fixedRotations: isEqual,
    deburrDoubleSided: isEqual,
    numThreads: isEqual,
    numCountersinks: isEqual,
    sheetTappingData: (lhsEntries, rhsEntries) => lhsEntries.length === rhsEntries.length && lhsEntries.every((lhsEntry, index) => {
        const rhsEntry = rhsEntries[index];
        return lhsEntry.screwThread.identifier === rhsEntry.screwThread.identifier
            && isEqual(lhsEntry.center3, rhsEntry.center3);
    }),
    articleUserData: isEqual,
    userDefinedScalePrices: isEqual,
    tubeMaterial: (lhs, rhs) => lhs === undefined && rhs === undefined ? true : lhs === undefined || rhs === undefined ? false : lhs.identifier === rhs.identifier,
    tubeSpecification: (lhs, rhs) => lhs === undefined && rhs === undefined ? true : lhs === undefined || rhs === undefined ? false : lhs.identifier === rhs.identifier,
    sheetFilterSheetIds: (lhs, rhs) => {
        const sort = (vec) => vec.filter((v, index, self) => self.indexOf(v) === index);
        return isEqual(sort(lhs), sort(rhs));
    },
    bendLineEngravingMode: isEqual,
};
function isCloseNodeUserDataEntry(key, lhs, rhs) {
    assertDebug(() => isNodeUserDataEntryType(key, lhs), `Pre-condition violated: lhs is incompatible to key ${key}: ${JSON.stringify(lhs)}`);
    assertDebug(() => isNodeUserDataEntryType(key, lhs), `Pre-condition violated: rhs is incompatible to key ${key}: ${JSON.stringify(rhs)}`);
    return isCloseNodeUserDataFunctionMap[key](lhs, rhs);
}
export function collectNodeUserDataEntries(key, vertex, accessFlag) {
    const contained = (flag, flagSet) => (flag & flagSet) === flag;
    const vertices = [];
    if (contained(userDataAccess.self, accessFlag)) {
        vertices.push(vertex);
    }
    if (contained(userDataAccess.reaching, accessFlag)) {
        vertices.push(...wsi4.graph.reaching(vertex));
    }
    if (contained(userDataAccess.reachable, accessFlag)) {
        vertices.push(...wsi4.graph.reachable(vertex));
    }
    if (contained(userDataAccess.article, accessFlag)) {
        vertices.push(...wsi4.graph.article(vertex));
    }
    return vertices.filter((lhs, index, self) => self.findIndex(rhs => isEqual(lhs, rhs)) === index)
        .filter(v => isCompatibleToNodeUserDataEntry(key, v))
        .map(v => getNodeUserDataEntry(key, v))
        .filter((lhs, index, self) => self.findIndex(rhs => (lhs === undefined && rhs === undefined)
        || (isNodeUserDataEntryType(key, lhs)
            && isNodeUserDataEntryType(key, rhs)
            && isCloseNodeUserDataEntry(key, lhs, rhs))) === index)
        .reduce((acc, value) => {
        if (value === undefined) {
            return acc;
        }
        else {
            acc.push(value);
            return acc;
        }
    }, []);
}
export function isUserData(a) {
    return isStringIndexedInterface(a);
}
export function mergeUserData(toExtend, toMerge) {
    return { ...toMerge,
        ...toExtend };
}
export function removeInvalidUserData(oldUserData, newUserData) {
    const result = {};
    for (const key of Object.keys(oldUserData)) {
        if (newUserData[key] !== undefined) {
            result[key] = oldUserData[key];
        }
    }
    return result;
}
export function mergeArticleAttributes(lhs, rhs) {
    const userData = mergeUserData(lhs.userData, rhs.userData);
    if (typeof lhs.userData["name"] !== "string") {
        userData["name"] = rhs.userData["name"];
    }
    return {
        userData: userData,
    };
}
export function hasManufacturingCostOverride(vertex, userData) {
    return getNodeUserDataEntryOrThrow("userDefinedScalePrices", vertex, userData).length > 0;
}
export function isCompatibleToNodeUserDataEntry(key, vertex) {
    const context = nodeUserDataContextForVertex(vertex);
    return isCompatibleToNodeUserDataEntryImpl(key, context);
}
function isNodeUserDataStringIndex(index, context) {
    return nodeUserDataEntries()
        .some(key => createNodeUserDataStringIndex(key, context) === index);
}
function toNodeUserDataEntryKey(context, userDataIndex) {
    return nodeUserDataEntries()
        .find(key => userDataIndex === createNodeUserDataStringIndex(key, context));
}
function toNodeUserDataEntryKeyOrThrow(context, userDataIndex) {
    const result = toNodeUserDataEntryKey(context, userDataIndex);
    return result === undefined ? wsi4.throwError(`"${userDataIndex}" is not a valid UserData key for vertex ${JSON.stringify(context)}`) : result;
}
function toVertexOrThrow(vertexKey) {
    const vertex = wsi4.graph.vertices()
        .find(v => wsi4.util.toKey(v) === vertexKey);
    return vertex === undefined ? wsi4.throwError("Vertex key invalid: " + JSON.stringify(vertexKey)) : vertex;
}
export function computeNodeProcessingState(vertexKey, oldUserData, newUserData) {
    const vertex = toVertexOrThrow(vertexKey);
    const context = nodeUserDataContextForVertex(vertex);
    if (!isStringIndexedInterface(oldUserData)) {
        return wsi4.throwError("Input invalid");
    }
    if (!isStringIndexedInterface(newUserData)) {
        return wsi4.throwError("Input invalid");
    }
    const result = getKeysOfObject(newUserData)
        .filter((userDataIndex) => isString(userDataIndex))
        .filter(userDataIndex => isNodeUserDataStringIndex(userDataIndex, context))
        .map((userDataIndex) => [
        userDataIndex,
        toNodeUserDataEntryKeyOrThrow(context, userDataIndex),
    ])
        .filter(([userDataIndex, key,]) => {
        const lhs = oldUserData[userDataIndex];
        const rhs = newUserData[userDataIndex];
        return !isNodeUserDataEntryType(key, lhs) || !isNodeUserDataEntryType(key, rhs) || !isCloseNodeUserDataEntry(key, lhs, rhs);
    })
        .map(([_, key,]) => key)
        .filter(key => isCompatibleToNodeUserDataEntry(key, vertex))
        .reduce((acc, key) => computeMinProcessingState(acc, nodeUserDataProcessingStateMap[key]), GraphNodeProcessingState.Finished);
    return result;
}
export function applyTubeToUserData(vertex, tube, tubeMaterialsInput, tubeSpecificationsInput, userDataInput) {
    const tubeMaterials = tubeMaterialsInput !== null && tubeMaterialsInput !== void 0 ? tubeMaterialsInput : getTable(TableType.tubeMaterial);
    const tubeSpecifications = tubeSpecificationsInput !== null && tubeSpecificationsInput !== void 0 ? tubeSpecificationsInput : getTable(TableType.tubeSpecification);
    const tubeMaterial = tubeMaterials.find(row => row.identifier === tube.tubeMaterialId);
    assert(tubeMaterial !== undefined, "Tables inconsistent");
    const tubeSpecification = tubeSpecifications.find(row => row.identifier === tube.tubeSpecificationId);
    assert(tubeSpecification !== undefined, "Tables inconsistent");
    let userData = userDataInput !== null && userDataInput !== void 0 ? userDataInput : wsi4.node.userData(vertex);
    userData = createUpdatedNodeUserData("tubeMaterial", vertex, tubeMaterial, userData);
    userData = createUpdatedNodeUserData("tubeSpecification", vertex, tubeSpecification, userData);
    return userData;
}
